import Head from "next/head";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Privacy() {
  return (
    <>
      <Head>
        <title>Privacy Policy - ClearCut</title>
        <meta name="description" content="ClearCut Privacy Policy - Learn how we collect, use, and protect your personal information." />
      </Head>
      <div className="min-h-screen bg-bg text-white flex flex-col">
        <Navbar />

        <motion.main
          className="mx-auto max-w-3xl flex-1 px-6 pb-20 pt-16"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-display text-4xl font-extrabold">Privacy Policy</h1>
          <p className="mt-2 text-sm text-muted">Last updated: February 15, 2026</p>

          <div className="mt-10 space-y-8 text-sm leading-relaxed text-muted">
            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">1. Information We Collect</h2>
              <p className="mb-2">We collect the following types of information:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li><strong className="text-white">Account information:</strong> Email address and password when you create an account</li>
                <li><strong className="text-white">Usage data:</strong> Number of images processed, subscription tier, and feature usage</li>
                <li><strong className="text-white">Uploaded images:</strong> Images you upload for background removal processing</li>
                <li><strong className="text-white">Payment information:</strong> Processed securely by Stripe. We do not store credit card numbers.</li>
                <li><strong className="text-white">Technical data:</strong> IP address, browser type, and device information for security and analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">2. How We Use Your Information</h2>
              <ul className="list-disc space-y-1 pl-5">
                <li>To provide and improve the background removal service</li>
                <li>To manage your account and process payments</li>
                <li>To enforce usage limits and prevent abuse</li>
                <li>To send transactional emails (password resets, billing notifications)</li>
                <li>To analyze usage patterns and improve the Service</li>
              </ul>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">3. Image Storage and Processing</h2>
              <p>
                Uploaded images and processed results are stored in our cloud infrastructure. Original and processed images are retained in your account until you delete them or close your account. We do not use your images to train AI models or share them with third parties.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">4. Third-Party Services</h2>
              <p className="mb-2">We use the following third-party services:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li><strong className="text-white">Supabase:</strong> Authentication, database, and file storage</li>
                <li><strong className="text-white">Stripe:</strong> Payment processing</li>
                <li><strong className="text-white">Vercel:</strong> Frontend hosting</li>
                <li><strong className="text-white">Railway:</strong> Backend hosting</li>
              </ul>
              <p className="mt-2">
                Each of these services has its own privacy policy governing how they handle your data.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">5. Data Security</h2>
              <p>
                We implement industry-standard security measures to protect your data, including encryption in transit (HTTPS/TLS), secure authentication via Supabase, and Row Level Security on our database. However, no system is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">6. Data Retention</h2>
              <p>
                We retain your account data and images for as long as your account is active. If you delete your account, we will delete your personal data and images within 30 days, except where retention is required by law.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">7. Your Rights</h2>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and associated data</li>
                <li>Export your data</li>
                <li>Object to data processing</li>
              </ul>
              <p className="mt-2">
                To exercise any of these rights, contact us at support@clearcut.io.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">8. Cookies</h2>
              <p>
                We use essential cookies for authentication and session management. We do not use tracking cookies for advertising. If analytics are enabled, they use privacy-friendly, cookie-free tracking.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">9. Children&apos;s Privacy</h2>
              <p>
                The Service is not intended for children under 13. We do not knowingly collect data from children under 13. If we learn that we have collected such data, we will promptly delete it.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of material changes via email or in-app notification. Continued use of the Service after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="mb-3 font-display text-lg font-bold text-white">11. Contact</h2>
              <p>
                For questions about this Privacy Policy, please contact us at support@clearcut.io.
              </p>
            </section>
          </div>
        </motion.main>

        <Footer />
      </div>
    </>
  );
}
